import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, ArrowRight } from 'lucide-react';
import { apiService } from '@/lib/api';
import LenderShell, { LENDER_BLUE, LENDER_NAVY } from '@/components/lender/LenderShell';
import { getLenderActor, money, fmtDate, APP_STATUS } from '@/lib/lenderDemo';

export default function LenderApplications() {
  const actor = getLenderActor();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    if (!actor?.id) return;
    apiService.lenderApplications(actor.id).then(({ data }) => setData(data)).catch(() => setData({ applications: [] }));
  }, [actor?.id]);
  useEffect(() => { load(); }, [load]);

  const apps = data?.applications || [];

  return (
    <LenderShell active="applications">
      <h1 className="text-2xl font-bold" style={{ color: LENDER_NAVY }}>Loan applications</h1>
      <p className="text-slate-500 text-sm mb-5">Borrowers requesting construction financing. Review, request documents, approve and close — then funds go live for the borrower to draw.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-xs text-slate-500">Open applications</div><div className="text-xl font-bold mt-1" style={{ color: LENDER_NAVY }}>{apps.length}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-xs text-slate-500">Awaiting you</div><div className="text-xl font-bold mt-1" style={{ color: LENDER_BLUE }}>{data?.pending_count ?? 0}</div></div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
              <th className="px-4 py-3">Borrower / Project</th>
              <th className="px-4 py-3 text-right">Requested</th>
              <th className="px-4 py-3">Docs</th>
              <th className="px-4 py-3">Closing</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {apps.map((a) => {
              const chip = APP_STATUS[a.status] || APP_STATUS.in_progress;
              return (
                <tr key={a.id} data-testid={`lender-app-row-${a.id}`} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 cursor-pointer" onClick={() => navigate(`/lender/applications/${a.id}`)}>
                  <td className="px-4 py-3">
                    <div className="font-semibold" style={{ color: LENDER_NAVY }}>{a.borrower_name}</div>
                    <div className="text-xs text-slate-400">{a.project_name}{a.path === 'existing' && <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-600">existing user</span>}</div>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700">{money(a.requested_amount)}</td>
                  <td className="px-4 py-3 text-slate-500"><FileText className="w-3.5 h-3.5 inline mr-1 text-slate-400" />{a.provided_docs}/{a.doc_count || a.provided_docs}</td>
                  <td className="px-4 py-3 text-slate-600">{a.closing_date ? fmtDate(a.closing_date) : '—'}</td>
                  <td className="px-4 py-3"><span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: chip.bg, color: chip.fg }}>{chip.label}</span></td>
                  <td className="px-4 py-3 text-right"><ArrowRight className="w-4 h-4 text-slate-300" /></td>
                </tr>
              );
            })}
            {apps.length === 0 && (<tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No applications yet.</td></tr>)}
          </tbody>
        </table>
      </div>
    </LenderShell>
  );
}
